import type { LiveChange } from './actionCard.ts';

/**
 * The Action cards' shared, on-device state (decision 165; the walk of 4 Nov, BOOKING 2, 7 and 8):
 * - a card answered here (confirm, Looks Good, Withdraw, Open Lesson) flips in every copy of it at once, the
 *   list's and the pane's, not when the server's record comes back;
 * - a change this device just made (the player's move, the coach's sent times) shows its card at once, until
 *   the server's own record for it arrives.
 */
export interface LocalChange extends LiveChange { localAt: number }

let flips = new Map<string, LiveChange>();
let locals: LocalChange[] = [];
let version = 0;
const listeners = new Set<() => void>();
const emit = () => { version += 1; listeners.forEach((l) => l()); };

export function subscribeCards(l: () => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}
export const cardsVersion = (): number => version;

/** This card's answer here, or its undoing (null) when the server refused it. */
export function flipCard(id: string, next: LiveChange | null): void {
  flips = new Map(flips);
  if (next) flips.set(id, next); else flips.delete(id);
  emit();
}
export const flipOf = (id: string): LiveChange | undefined => flips.get(id);

/** A change made here, shown at once (its card is its own result or its waiting card). */
export function addLocalChange(c: LiveChange, nowMs = Date.now()): void {
  locals = [...locals.filter((x) => x.id !== c.id), { ...c, localAt: nowMs }];
  emit();
}
export function removeLocalChange(id: string): void {
  locals = locals.filter((x) => x.id !== id);
  emit();
}

/**
 * The local changes for these lessons still waiting on their record: one is gone once its own record arrives,
 * the same kind on the same lesson with the same new time (another record of that kind, the seed's or an older
 * move, never covers it), or after two minutes if none ever does.
 */
export function pendingLocals(records: Array<Pick<LiveChange, 'kind' | 'bookingId' | 'at' | 'to'>>, bookingIds: Set<string>, nowMs = Date.now()): LiveChange[] {
  const same = (a: string | undefined, b: string | undefined) => !!a && !!b && (a === b || Date.parse(a) === Date.parse(b));
  return locals.filter((l) => !!l.bookingId && bookingIds.has(l.bookingId) && nowMs - l.localAt < 120_000
    && !records.some((r) => r.kind === l.kind && r.bookingId === l.bookingId && same(r.to[0], l.to[0])));
}

/** For tests. */
export function resetCards(): void {
  flips = new Map(); locals = []; emit();
}
